import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Home } from "lucide-react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      setStatus("processing");
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-void-black flex items-center justify-center p-4 grid-bg">
      <div className="cyber-card p-8 max-w-md w-full text-center">
        {status === "loading" || status === "processing" ? (
          <>
            <Loader2 className="w-16 h-16 text-neon-blue mx-auto mb-6 animate-spin" />
            <h1 className="font-orbitron text-2xl text-white mb-4">PROCESSING PAYMENT</h1>
            <p className="text-gray-400 font-rajdhani">Please wait while we confirm your payment...</p>
          </>
        ) : status === "success" ? (
          <>
            <CheckCircle className="w-16 h-16 text-neon-green mx-auto mb-6" />
            <h1 className="font-orbitron text-2xl neon-text-blue mb-4">PAYMENT SUCCESSFUL!</h1>
            <p className="text-gray-400 font-rajdhani mb-2">Thank you for your purchase!</p>
            {paymentData && (
              <p className="text-neon-pink font-orbitron text-lg mb-6">
                ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <p className="text-gray-500 text-sm mb-8">A confirmation email will be sent to you shortly.</p>
            <button 
              onClick={() => navigate('/')}
              className="cyber-btn"
              data-testid="back-home-btn"
            >
              <span className="flex items-center gap-2">
                <Home className="w-4 h-4" />
                BACK TO HOME
              </span>
            </button>
          </>
        ) : status === "expired" ? (
          <>
            <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="font-orbitron text-2xl text-yellow-500 mb-4">SESSION EXPIRED</h1>
            <p className="text-gray-400 font-rajdhani mb-8">Your payment session has expired. Please try again.</p>
            <button 
              onClick={() => navigate('/')}
              className="cyber-btn"
              data-testid="try-again-btn"
            >
              <span>TRY AGAIN</span>
            </button>
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-orbitron text-2xl text-red-500 mb-4">PAYMENT ERROR</h1>
            <p className="text-gray-400 font-rajdhani mb-8">Something went wrong. Please contact support if the issue persists.</p>
            <button 
              onClick={() => navigate('/')}
              className="cyber-btn"
              data-testid="return-home-btn"
            >
              <span>RETURN HOME</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
